
import { RecipeFeedback } from "@/types/recipe";
import { supabase } from "../../integrations/supabase/client";
import { handleServiceError, withErrorHandling } from "../utils/errorHandling";

export const feedbackMutationService = {
  /**
   * Saves feedback for a recipe
   * @param recipeId The ID of the recipe to save feedback for
   * @param comment The feedback comment
   * @param rating The feedback rating (1-5)
   * @returns The saved feedback object or null if there was an error
   */
  saveFeedback: withErrorHandling(
    async (recipeId: string, comment: string, rating: number): Promise<RecipeFeedback | null> => {
      // Get the current user
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error("User not authenticated");
      }
      
      // Use user email or id as username
      const username = user.email?.split('@')[0] || user.id;
      
      // Get user first name from profiles table
      const { data: profileData, error: profileError } = await supabase
        .from("profiles")
        .select("first_name")
        .eq("id", user.id)
        .single();
      
      if (profileError) {
        console.error("Error fetching profile data:", profileError);
      }

      // Use first name from profile or fallback to username
      const firstName = profileData?.first_name || username;
      
      const { data, error } = await supabase
        .from("recipe_feedbacks")
        .insert({
          recipe_id: recipeId,
          comment: comment,
          rating: rating,
          username: username,
          user_id: user.id,
          first_name: firstName // Store first name directly in the feedback
        })
        .select()
        .single();

      if (error) {
        throw error;
      }

      // Convert from database format to RecipeFeedback type
      const savedFeedback: RecipeFeedback = {
        id: data.id,
        recipeId: data.recipe_id,
        username: data.username,
        userId: data.user_id,
        firstName: data.first_name, // Use the stored first name
        comment: data.comment,
        rating: data.rating,
        createdAt: data.created_at,
      };

      return savedFeedback;
    },
    "Error saving feedback"
  )
};
